import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LabelingContainer } from '~/components/elements/LabelingContainer'
import {
  CustomerAttributeGroupSelector,
  type CustomerAttributeGroupSelectorProps,
} from '~/components/features/CustomerAttributeGroupSelector'
import { useFormField } from '~/hooks/useFormField'
import { findByPath } from '~/utils'

import {
  type SurveyGroupSurvey,
  useMainQuerySurveys,
} from '../../hooks/useMainQuery'

import { type InputValues } from './utils'

export type AttachedCustomerAttributeGroupSelectorProps = {
  index: number
  survey: SurveyGroupSurvey
}

export const AttachedCustomerAttributeGroupSelector = ({
  index,
  survey,
}: AttachedCustomerAttributeGroupSelectorProps) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<InputValues>()
  const field = useFormField(
    control,
    `customerAttributeGroups.${index}.attachedCustomerAttributeGroupMap.${survey.id}`,
  )

  const surveys = useMainQuerySurveys()
  const customerAttributeGroups = React.useMemo(() => {
    const survey_ = findByPath(surveys, 'id', survey.id)

    return survey_?.customerAttributeGroups ?? []
  }, [survey.id, surveys])

  const handleChange = React.useCallback<
    EH<
      CustomerAttributeGroupSelectorProps<
        (typeof customerAttributeGroups)[number]
      >,
      'onChange'
    >
  >(
    (customerAttributeGroupId) => {
      field.onChange(customerAttributeGroupId)
      setValue(`customerAttributeGroups.${index}.customerAttributes`, [])
    },
    [field, index, setValue],
  )

  return (
    <LabelingContainer label={survey.title} labelVariant="small">
      <CustomerAttributeGroupSelector
        customerAttributeGroups={customerAttributeGroups}
        isNullSelectable={true}
        nullText={t('no_corresponding_customized_segments_group')}
        {...field}
        onChange={handleChange}
      />
    </LabelingContainer>
  )
}
